/**
 * Client ของ `POST /v1/auth/logout` — contract ที่ freeze ไว้ใน
 * `docs/API_CONTRACT.md` (D-057)
 *
 * หลักการเดียวกับ `client.ts` และ `passwordClient.ts`: ฟังก์ชันนี้ **ไม่ throw**
 * ทุกคำตอบกลายเป็นสถานะที่ผู้เรียกแจกแจงเอง จะได้ไม่มีใคร `catch` แล้วเดาว่าสำเร็จ
 *
 * สามข้อที่ต่างจาก client อื่นและตั้งใจให้ต่าง:
 *
 * 1. **ไม่ผ่าน `authorizedFetch`** ทั้งที่เป็น endpoint ที่ต้อง login — ตัวนั้นแปล
 *    `401` ทุกใบเป็น `endSession('UNAUTHORIZED')` ซึ่งถูกสำหรับ session ที่ถูก revoke
 *    แต่ **ผิด** ที่นี่: contract ระบุว่า `401` ของ logout แปลว่า "ออกจากระบบอยู่แล้ว"
 *    การปล่อยให้ trap ตัวนั้นทำงาน จะทำให้คนที่กดออกจากระบบเองเห็นหน้า Login ที่บอกว่า
 *    "เซสชันก่อนหน้านี้ใช้ไม่ได้แล้ว" ทั้งที่เขาเป็นคนสั่งออกเอง — เหตุผลเดียวกับที่
 *    `passwordClient.ts` เลี่ยง wrapper ตัวนี้ (WO-P1-M10-007)
 * 2. **token มาจากผู้เรียก ไม่ได้มาจาก session store** — D-057 ระบุว่า endpoint นี้รับ
 *    `password_change_token` ของ D-050 ด้วย ซึ่งไม่เคยอยู่ใน store/storage เลย
 * 3. **ไม่มี request body และ `204` ไม่มี body ให้ parse** — ความสำเร็จอ่านจาก status
 *    เท่านั้น และคำขอนี้จึงไม่ส่ง `Content-Type` ไปด้วย เพราะไม่มีอะไรให้ประกาศชนิด
 *
 * ⚠️ token เป็นความลับระดับเดียวกับรหัสผ่าน (`docs/SECURITY.md`) ห้ามใส่ลงในค่าที่
 * คืนกลับ ลง log หรือลง error ใด ๆ — สถานะทุกตัวด้านล่างจึงไม่มีที่ให้ token อยู่
 */

import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/** เส้นทางจริงที่ client เรียก — ตาราง endpoint ใน contract ตัด base prefix `/v1` ออก */
export const LOGOUT_PATH = '/v1/auth/logout';

export type LogoutResult =
  /** `204 No Content` — เซิร์ฟเวอร์ revoke session ที่เรียกมาแล้ว */
  | { readonly status: 'ended' }
  /**
   * `401` — **ถือเป็นความสำเร็จ** ตาม contract: token ตายไปแล้ว (หมดอายุ/ถูก revoke)
   * ปลายทางที่ผู้ใช้ต้องการคือ "ไม่มี session นี้แล้ว" ซึ่งเป็นจริงอยู่ก่อนแล้ว
   * ผู้เรียกต้องไม่แสดงสถานะนี้เป็นข้อผิดพลาด
   */
  | { readonly status: 'already-ended' }
  /** คำตอบอื่นที่ไม่อยู่ใน contract — ไม่เดาแทนเซิร์ฟเวอร์ว่า session ยังอยู่หรือไม่ */
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string };

export interface LogoutOptions {
  /**
   * token ที่จะปิด — **บังคับ** และมีได้สองแบบตาม D-057:
   *
   * - session token ปกติจาก `session.ts`
   * - `password_change_token` ของ D-050 (เส้นทางกู้คืนที่ผู้ใช้เปลี่ยนใจ)
   */
  readonly token: string;
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ */
  readonly fetchImpl?: typeof fetch;
}

/**
 * ปิด session ที่ token ใบนี้ถืออยู่ — **เฉพาะใบนี้ ไม่ใช่ทุกเครื่องของคนนั้น**
 *
 * ฟังก์ชันนี้ไม่แตะ session store และไม่ล้าง storage เอง: ผู้เรียกเป็นคนตัดสินว่า
 * จะล้างอะไรฝั่ง client และต้องล้างเสมอไม่ว่าฟังก์ชันนี้จะคืนสถานะไหน (ดู `signOut`)
 */
export async function logout(options: LogoutOptions): Promise<LogoutResult> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, LOGOUT_PATH);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        // ประกอบเองแทนที่จะพึ่ง `authorizedFetch` — ดู doc ของโมดูลข้อ 1 และ 2
        Authorization: `Bearer ${options.token}`,
      },
      // ไม่มี `body` โดยเจตนา — contract ระบุว่า endpoint นี้ไม่รับ request body
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // ส่งต่อเฉพาะ `message` ไม่ใช่ทั้ง error object — header ที่มี token อยู่ต้องไม่มี
    // ทางติดไปกับสถานะที่ผู้เรียกอาจเอาไป log
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (response.status === 204) {
    return { status: 'ended' };
  }

  if (response.status === 401) {
    return { status: 'already-ended' };
  }

  // `2xx` ที่ไม่ใช่ `204` ไม่อยู่ใน contract ของ endpoint นี้ — ถือว่าเซิร์ฟเวอร์ตัวนั้น
  // ไม่ใช่ตัวที่ contract อธิบายไว้ ดีกว่ารายงานว่าปิด session สำเร็จโดยไม่รู้จริง
  return { status: 'server-error', httpStatus: response.status };
}
